import { useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

function subscribeToNetwork(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

function getOnlineSnapshot(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

export function QueryStatusBanner() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isOnline = useSyncExternalStore(
    subscribeToNetwork,
    getOnlineSnapshot,
    () => true,
  );
  const hasStaleQueryError = useSyncExternalStore(
    (onChange) => queryClient.getQueryCache().subscribe(() => onChange()),
    () => queryClient.getQueryCache().getAll().some(
      (query) => query.state.status === 'error' && query.state.data !== undefined,
    ),
    () => false,
  );

  if (isOnline && !hasStaleQueryError) return null;

  return (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-2 text-sm ${
        isOnline
          ? 'bg-md-error-container text-md-on-surface'
          : 'bg-md-secondary-container text-md-on-secondary-container'
      }`}
      role="status"
      aria-live="polite"
    >
      <span>
        <strong>{isOnline ? t('errors.staleData') : t('errors.offlineTitle')}</strong>{' '}
        {!isOnline && t('errors.offlineCached')}
      </span>
      {isOnline && (
        <button
          type="button"
          className="shrink-0 font-semibold underline"
          onClick={() => void queryClient.refetchQueries({ type: 'active' })}
        >
          {t('errors.retrySync')}
        </button>
      )}
    </div>
  );
}
